<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { Channel, MediaAsset } from '../../api/types'
import { useInbox } from '../../stores/inbox'
import { useLibrary } from '../../stores/library'
import MediaGrid from '../library/MediaGrid.vue'
import Icon from '../ui/Icon.vue'

/**
 * Library panel above the composer (image button): pick several pictures/videos to send with the next message,
 * or add new ones (upload button, drag & drop). Picking stages straight into the composer strip.
 */
const props = defineProps<{ convId: string; channel: Channel | null }>()
const emit = defineEmits<{ close: []; files: [files: File[]] }>()
const lib = useLibrary()
const inbox = useInbox()
const panel = ref<HTMLElement | null>(null)

const selected = computed(() => lib.stagedFor(props.convId).map((s) => s.asset.id))
const max = computed(() => (props.channel === 'line' ? 4 : 10))

function toggle(a: MediaAsset): void {
  const on = selected.value.includes(a.id)
  if (!on && selected.value.length >= max.value) {
    inbox.flash(
      props.channel === 'line'
        ? 'LINE ส่งได้ครั้งละ 5 ข้อความ (ข้อความ 1 + รูปหรือวิดีโอ 4)'
        : `เลือกได้ครั้งละไม่เกิน ${max.value} รายการ`,
    )
    return
  }
  lib.toggle(props.convId, a)
}

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') emit('close')
}

function onDown(e: MouseEvent): void {
  const t = e.target as Node
  if (panel.value && !panel.value.contains(t) && !(t as Element).closest?.('[aria-label="คลังรูปและวิดีโอ"]')) emit('close')
}

onMounted(() => {
  document.addEventListener('keydown', onKey)
  document.addEventListener('mousedown', onDown)
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey)
  document.removeEventListener('mousedown', onDown)
})
</script>

<template>
  <section ref="panel" class="ld" role="dialog" aria-label="คลังรูปและวิดีโอ">
    <div class="ld-head">
      <Icon name="image" />
      <h3>คลังรูปและวิดีโอ</h3>
      <span class="ld-sub">กดเลือกได้หลายรายการ ส่งต่อจากข้อความ</span>
      <span class="grow"></span>
      <button type="button" class="ld-close" aria-label="ปิดคลังรูป" @click="emit('close')"><Icon name="x" /></button>
    </div>
    <div class="ld-body">
      <MediaGrid
        :items="lib.media"
        :folders="lib.folders"
        :selectable="true"
        :selected="selected"
        :channel="channel"
        :compact="true"
        :uploads="lib.uploadsFor(convId)"
        :can-upload="lib.uploadsEnabled"
        :loading="lib.mediaLoading"
        empty-text="ยังไม่มีรูปในคลัง กดอัปโหลด หรือลากรูปมาวางที่นี่"
        @toggle="toggle"
        @files="emit('files', $event)"
        @dismiss-upload="lib.dismissUpload($event)"
      />
    </div>
    <div class="ld-foot">
      <span class="ld-count">{{ selected.length ? `เลือกแล้ว ${selected.length} รายการ` : 'ยังไม่ได้เลือก' }}</span>
      <span v-if="channel === 'line'" class="ld-note">LINE ส่งได้ครั้งละ 5 ข้อความ รวมข้อความ 1</span>
      <span class="grow"></span>
      <button type="button" class="ld-done" @click="emit('close')">เสร็จ</button>
    </div>
  </section>
</template>

<style scoped>
.ld {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: calc(100% - 4px);
  z-index: 20;
  display: flex;
  flex-direction: column;
  height: min(460px, 62vh);
  background: #fff;
  border: 1px solid #cfe3d8;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(23, 33, 28, 0.14);
  overflow: hidden;
}
.ld-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px 8px 14px;
  border-bottom: 1px solid var(--line2);
}
.ld-head > :deep(.ic) {
  width: 16px;
  height: 16px;
  color: var(--ac);
}
.ld-head h3 {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 21px;
}
.ld-sub {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.ld-close {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: var(--mut);
}
.ld-close:hover {
  background: #eef2f0;
  color: var(--ink);
}
.ld-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 14px;
}
.ld-foot {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px 8px 14px;
  border-top: 1px solid var(--line2);
}
.ld-count {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}
.ld-note {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.ld-done {
  height: 32px;
  padding: 0 16px;
  border-radius: 8px;
  background: var(--ac);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}
.ld-done:hover {
  background: var(--ac2);
}
</style>
